import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './AdminDashboard.css';
import { 
    FaChartBar, 
    FaBlog, 
    FaCalendarAlt, 
    FaEnvelope, 
    FaUsers,
    FaSignOutAlt,
    FaEye,
    FaEdit,
    FaTrash,
    FaChevronRight,
    FaBars,
    FaTimes,
    FaUserCircle,
    FaHome,
    FaBell,
    FaSearch,
    FaCog,
    FaUser,
    FaEnvelopeOpen,
    FaCalendar,
    FaKey,
    FaEdit as FaEditIcon,
    FaTimes as FaTimesCircle
} from 'react-icons/fa';
import { 
    BsCardChecklist,
    BsArrowUpRight,
    BsThreeDotsVertical
} from 'react-icons/bs';
import {
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import { authAPI, blogAPI, eventAPI, contactAPI } from '../../../api';

const AdminDashboard = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [user, setUser] = useState(null);
    const [stats, setStats] = useState({
        blogs: { total: 0, published: 0, draft: 0 },
        events: { total: 0, upcoming: 0, past: 0 },
        contacts: { total: 0, unread: 0 },
        users: 0
    });
    const [recentBlogs, setRecentBlogs] = useState([]);
    const [recentEvents, setRecentEvents] = useState([]);
    const [recentContacts, setRecentContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showProfilePopup, setShowProfilePopup] = useState(false);
    const [showEditProfile, setShowEditProfile] = useState(false);
    const [editForm, setEditForm] = useState({
        name: '',
        email: ''
    });
    const [editLoading, setEditLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchDashboardData();
        fetchUserProfile();
    }, []);

    const fetchUserProfile = async () => {
        try {
            const response = await authAPI.getProfile();
            if (response.success && response.user) {
                setUser(response.user);
                setEditForm({
                    name: response.user.name,
                    email: response.user.email
                });
            }
        } catch (error) {
            console.error('Erreur lors du chargement du profil:', error);
        }
    };

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            
            const [blogsRes, eventsRes, contactsRes] = await Promise.allSettled([
                blogAPI.getMyBlogs(1),
                eventAPI.getMyEvents(1),
                contactAPI.getAll(1)
            ]);

            let blogsData = [];
            if (blogsRes.status === 'fulfilled' && blogsRes.value.success) {
                blogsData = blogsRes.value.data?.data || [];
            }

            let eventsData = [];
            if (eventsRes.status === 'fulfilled' && eventsRes.value.success) {
                eventsData = eventsRes.value.data?.data || [];
            }

            let contactsData = [];
            let unreadCount = 0;
            if (contactsRes.status === 'fulfilled' && contactsRes.value.success) {
                contactsData = contactsRes.value.data?.data || [];
                const unreadRes = await contactAPI.getUnreadCount();
                if (unreadRes.success) {
                    unreadCount = unreadRes.count || 0;
                }
            }

            const publishedBlogs = blogsData.filter(blog => blog.is_published).length;
            const upcomingEvents = eventsData.filter(event => 
                new Date(event.end_date) >= new Date()
            ).length;

            setStats({
                blogs: {
                    total: blogsData.length,
                    published: publishedBlogs,
                    draft: blogsData.length - publishedBlogs
                },
                events: {
                    total: eventsData.length,
                    upcoming: upcomingEvents,
                    past: eventsData.length - upcomingEvents
                },
                contacts: {
                    total: contactsData.length,
                    unread: unreadCount
                },
                users: 0
            });

            setRecentBlogs(blogsData.slice(0, 3));
            setRecentEvents(eventsData.slice(0, 3));
            setRecentContacts(contactsData.slice(0, 3));

        } catch (error) {
            console.error('Erreur lors du chargement du dashboard:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        authAPI.logout();
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('userRole');
        navigate('/connexion');
    };

    const toggleProfilePopup = () => {
        setShowProfilePopup(!showProfilePopup);
        setShowEditProfile(false);
    };

    const handleEditProfile = async (e) => {
        e.preventDefault();
        try {
            setEditLoading(true);
            const response = await authAPI.updateProfile(editForm);
            
            if (response.success) {
                setUser(response.user);
                setShowEditProfile(false);
                alert('Profil mis à jour avec succès');
            }
        } catch (error) {
            console.error('Erreur lors de la mise à jour du profil:', error);
            alert(error.message || 'Erreur lors de la mise à jour');
        } finally {
            setEditLoading(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const menuItems = [
        { path: '/admin', icon: <FaChartBar />, label: 'Dashboard', active: true },
        { path: '/admin/blogs', icon: <FaBlog />, label: 'Blogs', badge: stats.blogs.unread },
        { path: '/admin/evenements', icon: <FaCalendarAlt />, label: 'Événements' },
        { path: '/admin/contacts', icon: <FaEnvelope />, label: 'Messages', badge: stats.contacts.unread },
        { path: '/admin/utilisateurs', icon: <FaUsers />, label: 'Utilisateurs' },
    ];

    const trafficData = [
        { name: 'Lun', visites: 2400 },
        { name: 'Mar', visites: 1398 },
        { name: 'Mer', visites: 9800 },
        { name: 'Jeu', visites: 3908 },
        { name: 'Ven', visites: 4800 },
        { name: 'Sam', visites: 3800 },
        { name: 'Dim', visites: 4300 },
    ];

    const categoryData = [
        { name: 'Éducation', value: 400 },
        { name: 'Technologie', value: 300 },
        { name: 'Culture', value: 300 },
        { name: 'Actualités', value: 200 },
    ];

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

    if (loading) {
        return (
            <div className="dashboard-loading">
                <div className="loading-spinneradmin">
                    <div className="spinneradmin"></div>
                    <p>Chargement du tableau de bord...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-dashboard-wrapper">
            {/* Sidebar */}
            <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
                <div className="sidebar-header">
                    <div className="logo">
                        <FaChartBar className="logo-icon" />
                        <span className="logo-text">ETEF Admin</span>
                    </div>
                    <button className="close-sidebar" onClick={() => setSidebarOpen(false)}>
                        <FaTimes />
                    </button>
                </div>

                <div 
                    className="user-profile clickable" 
                    onClick={toggleProfilePopup}
                    style={{ cursor: 'pointer' }}
                >
                    <div className="profile-image">
                        <FaUserCircle />
                    </div>
                    <div className="profile-info">
                        <h4>{user?.name || 'Administrateur'}</h4>
                        <p>{user?.email || 'admin@etef.com'}</p>
                        <span className="user-role-badge">
                            {user?.role === 'admin' ? 'Administrateur' : 'Utilisateur'}
                        </span>
                    </div>
                </div>

                <nav className="sidebar-nav">
                    {menuItems.map((item, index) => (
                        <Link
                            key={index}
                            to={item.path}
                            className={`nav-item ${item.active ? 'active' : ''}`}
                            onClick={() => setSidebarOpen(false)}
                        >
                            <span className="nav-icon">{item.icon}</span>
                            <span className="nav-label">{item.label}</span>
                            {item.badge && item.badge > 0 && (
                                <span className="nav-badge">{item.badge}</span>
                            )}
                            <FaChevronRight className="nav-arrow" />
                        </Link>
                    ))}
                </nav>

                <div className="sidebar-footer">
                    <button className="logout-btn" onClick={handleLogout}>
                        <FaSignOutAlt className="logout-icon" />
                        <span>Déconnexion</span>
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="main-content">
                {/* Topbar */}
                <header className="topbar">
                    <div className="topbar-left">
                        <button 
                            className="menu-toggle" 
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                        >
                            <FaBars />
                        </button>
                        <div className="page-title">
                            <h1>Tableau de Bord</h1>
                            <p>Bienvenue, {user?.name || 'Admin'} 👋</p>
                        </div>
                    </div>

                    <div className="topbar-right">
                        <div className="search-box">
                            <FaSearch className="search-icon" />
                            <input type="text" placeholder="Rechercher..." />
                        </div>
                        <button className="notification-btn">
                            <FaBell />
                            <span className="notification-badge">3</span>
                        </button>
                        <button className="settings-btn">
                            <FaCog />
                        </button>
                        
                        {/* User Profile Button */}
                        <div className="user-profile-btn-container">
                            <button 
                                className="user-profile-btn"
                                onClick={toggleProfilePopup}
                            >
                                <FaUserCircle className="user-avatar" />
                                <span className="user-name">
                                    {user?.name?.split(' ')[0] || 'Admin'}
                                </span>
                            </button>
                        </div>
                    </div>
                </header>

                {/* Profile Popup */}
                {showProfilePopup && (
                    <div className="profile-popup-overlay" onClick={toggleProfilePopup}>
                        <div className="profile-popup" onClick={(e) => e.stopPropagation()}>
                            <div className="profile-popup-header">
                                <h2>Profil Utilisateur</h2>
                                <button 
                                    className="close-popup"
                                    onClick={toggleProfilePopup}
                                >
                                    <FaTimesCircle />
                                </button>
                            </div>

                            <div className="profile-popup-content">
                                {!showEditProfile ? (
                                    <>
                                        <div className="profile-info-section">
                                            <div className="profile-avatar-large">
                                                <FaUserCircle />
                                            </div>
                                            <div className="profile-details">
                                                <h3>{user?.name || 'Non spécifié'}</h3>
                                                <div className="profile-detail-item">
                                                    <FaEnvelopeOpen className="detail-icon" />
                                                    <span>{user?.email || 'Non spécifié'}</span>
                                                </div>
                                                <div className="profile-detail-item">
                                                    <FaUser className="detail-icon" />
                                                    <span className={`role-badge ${user?.role}`}>
                                                        {user?.role === 'admin' ? 'Administrateur' : 'Utilisateur'}
                                                    </span>
                                                </div>
                                                <div className="profile-detail-item">
                                                    <FaCalendar className="detail-icon" />
                                                    <span>Membre depuis le {formatDate(user?.created_at || new Date())}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="profile-stats">
                                            <div className="profile-stat">
                                                <h4>Statut</h4>
                                                <p className={`status ${user?.role}`}>
                                                    {user?.role === 'admin' ? 'Administrateur' : 'Membre'}
                                                </p>
                                            </div>
                                            <div className="profile-stat">
                                                <h4>Dernière connexion</h4>
                                                <p>{formatDate(user?.updated_at || new Date())}</p>
                                            </div>
                                        </div>

                                        <div className="profile-actions">
                                            <button 
                                                className="profile-action-btn edit"
                                                onClick={() => setShowEditProfile(true)}
                                            >
                                                <FaEditIcon /> Modifier le profil
                                            </button>
                                            <button 
                                                className="profile-action-btn password"
                                                onClick={() => navigate('/admin/change-password')}
                                            >
                                                <FaKey /> Changer le mot de passe
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="edit-profile-form">
                                        <h3>Modifier le profil</h3>
                                        <form onSubmit={handleEditProfile}>
                                            <div className="form-group">
                                                <label htmlFor="name">Nom complet</label>
                                                <input
                                                    type="text"
                                                    id="name"
                                                    value={editForm.name}
                                                    onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                                                    placeholder="Votre nom"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="email">Adresse email</label>
                                                <input
                                                    type="email"
                                                    id="email"
                                                    value={editForm.email}
                                                    onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                                                    placeholder="Votre email"
                                                />
                                            </div>
                                            <div className="form-actions">
                                                <button 
                                                    type="button"
                                                    className="btn-cancel"
                                                    onClick={() => setShowEditProfile(false)}
                                                    disabled={editLoading}
                                                >
                                                    Annuler
                                                </button>
                                                <button 
                                                    type="submit"
                                                    className="btn-save"
                                                    disabled={editLoading}
                                                >
                                                    {editLoading ? 'Enregistrement...' : 'Enregistrer'}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Stats Cards */}
                <div className="stats-grid">
                    <div className="stat-card stat-primary">
                        <div className="stat-icon">
                            <FaBlog />
                        </div>
                        <div className="stat-content">
                            <h3>{stats.blogs.total}</h3>
                            <p>Articles de Blog</p>
                            <div className="stat-details">
                                <span className="detail-item">
                                    <span className="dot published"></span>
                                    {stats.blogs.published} publiés
                                </span>
                                <span className="detail-item">
                                    <span className="dot draft"></span>
                                    {stats.blogs.draft} brouillons
                                </span>
                            </div>
                        </div>
                        <Link to="/admin/blogs" className="stat-link">
                            <BsArrowUpRight />
                        </Link>
                    </div>

                    <div className="stat-card stat-success">
                        <div className="stat-icon">
                            <FaCalendarAlt />
                        </div>
                        <div className="stat-content">
                            <h3>{stats.events.total}</h3>
                            <p>Événements</p>
                            <div className="stat-details">
                                <span className="detail-item">
                                    <span className="dot upcoming"></span>
                                    {stats.events.upcoming} à venir
                                </span>
                                <span className="detail-item">
                                    <span className="dot past"></span>
                                    {stats.events.past} passés
                                </span>
                            </div>
                        </div>
                        <Link to="/admin/evenements" className="stat-link">
                            <BsArrowUpRight />
                        </Link>
                    </div>

                    <div className="stat-card stat-warning">
                        <div className="stat-icon">
                            <FaEnvelope />
                        </div>
                        <div className="stat-content">
                            <h3>{stats.contacts.total}</h3>
                            <p>Messages</p>
                            <div className="stat-details">
                                <span className="detail-item">
                                    <span className="dot unread"></span>
                                    {stats.contacts.unread} non lus
                                </span>
                                <span className="detail-item">
                                    <span className="dot read"></span>
                                    {stats.contacts.total - stats.contacts.unread} lus
                                </span>
                            </div>
                        </div>
                        <Link to="/admin/contacts" className="stat-link">
                            <BsArrowUpRight />
                        </Link>
                    </div>

                    <div className="stat-card stat-info">
                        <div className="stat-icon">
                            <FaUsers />
                        </div>
                        <div className="stat-content">
                            <h3>{stats.users}</h3>
                            <p>Utilisateurs</p>
                            <div className="stat-details">
                                <span className="detail-item">
                                    <span className="dot active"></span>
                                    0 actifs
                                </span>
                                <span className="detail-item">
                                    <span className="dot inactive"></span>
                                    0 inactifs
                                </span>
                            </div>
                        </div>
                        <Link to="/admin/utilisateurs" className="stat-link">
                            <BsArrowUpRight />
                        </Link>
                    </div>
                </div>

                {/* Charts Section */}
                <div className="charts-section">
                    <div className="chart-card">
                        <div className="chart-header">
                            <h3>Trafic du site (7 derniers jours)</h3>
                            <button className="chart-options">
                                <BsThreeDotsVertical />
                            </button>
                        </div>
                        <div className="chart-container">
                            <ResponsiveContainer width="100%" height={250}>
                                <LineChart data={trafficData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="name" stroke="#666" />
                                    <YAxis stroke="#666" />
                                    <Tooltip 
                                        contentStyle={{
                                            backgroundColor: 'white',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '8px'
                                        }}
                                    />
                                    <Line 
                                        type="monotone" 
                                        dataKey="visites" 
                                        stroke="#3498db" 
                                        strokeWidth={2}
                                        dot={{ r: 4 }}
                                        activeDot={{ r: 6 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="chart-card">
                        <div className="chart-header">
                            <h3>Catégories de contenu</h3>
                            <button className="chart-options">
                                <BsThreeDotsVertical />
                            </button>
                        </div>
                        <div className="chart-container">
                            <ResponsiveContainer width="100%" height={250}>
                                <PieChart>
                                    <Pie
                                        data={categoryData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={80}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {categoryData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip 
                                        formatter={(value) => [`${value} articles`, 'Quantité']}
                                        contentStyle={{
                                            backgroundColor: 'white',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '8px'
                                        }}
                                    />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Recent Activity Section */}
                <div className="recent-activity">
                    {/* Recent Blogs */}
                    <div className="activity-card">
                        <div className="activity-header">
                            <h3>
                                <FaBlog className="activity-icon" />
                                Articles récents
                            </h3>
                            <Link to="/admin/blogs" className="view-all">
                                Voir tous <FaChevronRight />
                            </Link>
                        </div>
                        <div className="activity-list">
                            {recentBlogs.length > 0 ? (
                                recentBlogs.map(blog => (
                                    <div key={blog.id} className="activity-item">
                                        <div className="activity-info">
                                            <h4>{blog.title}</h4>
                                            <p>
                                                <span className={`status ${blog.is_published ? 'published' : 'draft'}`}>
                                                    {blog.is_published ? 'Publié' : 'Brouillon'}
                                                </span>
                                                • {new Date(blog.created_at).toLocaleDateString()}
                                            </p>
                                        </div>
                                        <div className="activity-actions">
                                            <Link to={`/admin/blogs/${blog.id}`} className="action-btn view">
                                                <FaEye />
                                            </Link>
                                            <Link to={`/admin/blogs/${blog.id}/modifier`} className="action-btn edit">
                                                <FaEdit />
                                            </Link>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="no-data">
                                    <p>Aucun article disponible</p>
                                    <Link to="/admin/blogs/nouveau" className="btn-create">
                                        Créer un article
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Recent Events */}
                    <div className="activity-card">
                        <div className="activity-header">
                            <h3>
                                <FaCalendarAlt className="activity-icon" />
                                Événements à venir
                            </h3>
                            <Link to="/admin/evenements" className="view-all">
                                Voir tous <FaChevronRight />
                            </Link>
                        </div>
                        <div className="activity-list">
                            {recentEvents.length > 0 ? (
                                recentEvents.map(event => (
                                    <div key={event.id} className="activity-item">
                                        <div className="activity-info">
                                            <h4>{event.title}</h4>
                                            <p>
                                                {event.location} • 
                                                {new Date(event.start_date).toLocaleDateString()}
                                            </p>
                                        </div>
                                        <div className="activity-actions">
                                            <Link to={`/admin/evenements/${event.id}`} className="action-btn view">
                                                <FaEye />
                                            </Link>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="no-data">
                                    <p>Aucun événement à venir</p>
                                    <Link to="/admin/evenements/nouveau" className="btn-create">
                                        Créer un événement
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Recent Contacts */}
                    <div className="activity-card">
                        <div className="activity-header">
                            <h3>
                                <FaEnvelope className="activity-icon" />
                                Messages récents
                            </h3>
                            <Link to="/admin/contacts" className="view-all">
                                Voir tous <FaChevronRight />
                            </Link>
                        </div>
                        <div className="activity-list">
                            {recentContacts.length > 0 ? (
                                recentContacts.map(contact => (
                                    <div key={contact.id} className="activity-item">
                                        <div className="activity-info">
                                            <h4>{contact.subject}</h4>
                                            <p>
                                                {contact.name} • 
                                                {new Date(contact.created_at).toLocaleDateString()}
                                                {!contact.is_read && <span className="unread-dot"></span>}
                                            </p>
                                        </div>
                                        <div className="activity-actions">
                                            <Link to={`/admin/contacts/${contact.id}`} className="action-btn view">
                                                <FaEye />
                                            </Link>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="no-data">
                                    <p>Aucun message disponible</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="quick-actions">
                    <h3>Actions rapides</h3>
                    <div className="actions-grid">
                        <Link to="/admin/blogs/nouveau" className="action-btn quick">
                            <FaBlog className="action-icon" />
                            <span>Nouvel article</span>
                        </Link>
                        <Link to="/admin/evenements/nouveau" className="action-btn quick">
                            <FaCalendarAlt className="action-icon" />
                            <span>Nouvel événement</span>
                        </Link>
                        <Link to="/admin/utilisateurs/nouveau" className="action-btn quick">
                            <FaUsers className="action-icon" />
                            <span>Nouvel utilisateur</span>
                        </Link>
                        <Link to="/admin/contacts" className="action-btn quick">
                            <FaEnvelope className="action-icon" />
                            <span>Voir messages</span>
                        </Link>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default AdminDashboard;